import React, { useCallback, useContext, useEffect, useRef } from "react";
import { Animated, Platform, StyleSheet, View } from "react-native";
import { PlayerContext } from "../../backend/PlayerContext";
import {
  getResourceAmount,
  getResourceCap,
  Resource,
} from "../../backend/Resources";
import { calculateIncomePerSecond } from "../../backend/timetick/IncomeOverTime";
import { formatNumber } from "../../utils/FormattingUtils";
import { AccessibilityContext } from "../utility/AccessibilityContext";
import { GameIcon } from "../utility/icons/GameIcon";
import { KeyboardListener } from "../utility/KeyboardListener";
import { Text } from "../utility/Text";

/*
 * This component displays a mana bar. However, it's a total hack and will
 * certainly not look ok if it's not full-screen-width.
 */
export default function ManaBar() {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);
  playerContextRef.current = playerContext;
  const mana = getResourceAmount(playerContext, Resource.Mana);
  const manaCap = getResourceCap(playerContext, Resource.Mana);
  const ratioFilled = mana / manaCap;

  const filledAnimWithNativeDriver = useRef(
    new Animated.Value(ratioFilled),
  ).current;
  const filledAnimWithoutNativeDriver = useRef(
    new Animated.Value(ratioFilled),
  ).current;

  const manaIncome = calculateIncomePerSecond(playerContext)?.Mana ?? 0;
  const incomeText =
    manaIncome != 0
      ? " (" +
        (manaIncome > 0 ? "+" : "") +
        formatNumber(manaIncome, { showDecimals: true }) +
        "/s)"
      : "";

  useEffect(() => {
    if (playerContext.global.doNotUseNativeDriver) {
      Animated.timing(filledAnimWithoutNativeDriver, {
        toValue: Math.max(ratioFilled, 1e-5),
        duration: 100,
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(filledAnimWithNativeDriver, {
        toValue: Math.max(ratioFilled, 1e-5),
        duration: 100,
        useNativeDriver: true,
      }).start();
    }
  }, [ratioFilled]);

  const accessibilityContext = useContext(AccessibilityContext);
  const accessibilityLabel = `Mana: ${formatNumber(mana)}/${formatNumber(
    manaCap,
  )} ${incomeText}`;
  const onKeyboard = useCallback((ev: KeyboardEvent) => {
    const state = playerContextRef.current;

    if (ev.shiftKey && ev.ctrlKey && ev.key == "M") {
      const mana = getResourceAmount(state, Resource.Mana);
      const manaCap = getResourceCap(state, Resource.Mana);
      const manaIncome = calculateIncomePerSecond(state)?.Mana ?? 0;
      const incomeText =
        manaIncome != 0
          ? " (" +
            (manaIncome > 0 ? "+" : "") +
            formatNumber(manaIncome, { showDecimals: true }) +
            "/s)"
          : "";
      const accessibilityLabel = `Mana: ${formatNumber(mana)}/${formatNumber(
        manaCap,
      )} ${incomeText}`;
      accessibilityContext.announceForAccessibility(accessibilityLabel);
    }
  }, []);
  return (
    <View style={styles.container}>
      <Animated.View
        key={playerContext.global.doNotUseNativeDriver ? "yes" : "no"}
        style={[
          styles.filled,
          {
            transform: [
              {
                scaleX: playerContext.global.doNotUseNativeDriver
                  ? filledAnimWithoutNativeDriver
                  : filledAnimWithNativeDriver,
              },
            ],
          },
        ]}
      />
      <View
        style={styles.row}
        accessible={true}
        accessibilityRole={Platform.OS !== "web" ? "header" : undefined}
        accessibilityLabel={accessibilityLabel}
      >
        <GameIcon icon="mana" style={styles.icon} />
        <Text style={styles.textOverlay}>
          {formatNumber(mana)}/{formatNumber(manaCap)}
          <Text style={styles.income}>{incomeText}</Text>
        </Text>
      </View>
      <KeyboardListener callback={onKeyboard} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "center",
    width: "100%",
    backgroundColor: "#014",
    height: 29,
  },
  filled: {
    backgroundColor: "#04c",
    position: "absolute",
    left: "-100%",
    top: 0,
    height: "100%",
    width: "200%",
  },
  textOverlay: {
    color: "#fff",
    fontWeight: "bold",
  },
  income: {
    color: "#fff",
    fontWeight: "normal",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
  },
  icon: {
    height: 16,
    width: 16,
    marginRight: 4,
  },
  accessibilityHidden: {
    width: 1,
    height: 1,
    position: "absolute",
    left: 0,
    top: 0,
  },
});
